import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Award, Crown, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { addonIsActive } from '@/lib/officialsApi';
import { CheckoutDialog, loadCatalog } from '@/components/billing/CheckoutDialog';
import { FeatureList, PlanCardShell, PlanToggle, Price, Swap, usdNum } from './PlanCardShell';

const OFFICIALS_POINTS = [
  'Officials database, portal & availability',
  'Assign officials with conflict & travel warnings',
  'Assignment emails + automatic 24h / 3h reminders',
  'Pay rates & payment tracking',
];

// Annual + Flexible bundle products from the card catalog, with the savings math.
export const useBundleOffer = () => {
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    loadCatalog().then((c) => {
      const annual = c.products.find((p) => p.id === 'bundle_annual');
      const flex = c.products.find((p) => p.id === 'bundle_flex');
      if (!annual || !flex) return;
      const [proM, off] = flex.includes;
      const firstYearFlex = proM.price * 12 + off.price;
      setOffer({ annual, flex, proMonthly: proM.price, officials: off.price, firstYearFlex, save: Math.round((firstYearFlex - annual.total) * 100) / 100 });
    }).catch(() => {});
  }, []);
  return offer;
};

const AnnualPrice = ({ o }) => (
  <>
    <Price amount={o.annual.total} unit="/year" testId="bundle-price-annual" />
    <span className="inline-block mt-3 rounded-full bg-[#009E49] text-white text-xs font-bold px-2.5 py-0.5" data-testid="bundle-savings">Save ${usdNum(o.save)}/year</span>
    <p className="text-sm text-slate-600 mt-2">One annual payment. Pro and Officials included.</p>
  </>
);

const FlexPrice = ({ o }) => (
  <>
    <Price amount={o.flex.total} unit="due today" testId="bundle-price-flexible" />
    <p className="text-sm text-slate-600 mt-3">Then <b>${usdNum(o.proMonthly)} USD/month</b> for Pro and <b>${usdNum(o.officials)} USD/year</b> for Officials.</p>
    <p className="text-xs text-slate-500 mt-1" data-testid="bundle-flex-first-year">First-year total ${usdNum(o.firstYearFlex)} USD if Pro stays active for 12 months.</p>
  </>
);

const TERMS = {
  annual: (o) => `Renews yearly at $${usdNum(o.annual.total)} USD. Card only.`,
  flexible: (o) => `Today's $${usdNum(o.flex.total)} covers your first Pro month and the Officials year. Pro renews monthly, Officials yearly, each managed on its own. Card only.`,
};

const ownedNote = (hasPro, hasOfficials) => (hasPro && hasOfficials ? 'Pro and Officials Scheduling are both on your account.'
  : hasPro ? 'You already have Pro. Add Officials Scheduling on its own below.'
    : hasOfficials ? 'You already have Officials Scheduling. Choose Pro on its own instead.' : null);

const Footer = ({ o, option, note, onStart }) => (
  <>
    <Swap active={option} items={{
      annual: <p className="text-xs text-slate-500 text-center mb-3" data-testid="bundle-terms-annual">{TERMS.annual(o)}</p>,
      flexible: <p className="text-xs text-slate-500 text-center mb-3" data-testid="bundle-terms-flexible">{TERMS.flexible(o)}</p>,
    }} />
    {note ? (
      <div className="rounded-xl bg-white border border-amber-200 px-4 py-3 text-sm text-slate-600 text-center" data-testid="bundle-owned-note">
        {note} <Link to="/billing" className="font-semibold text-[#00247D] underline underline-offset-2">Manage in Billing</Link>
      </div>
    ) : (
      <Button onClick={onStart} data-testid="bundle-subscribe-btn"
        className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold shadow-md transition-[background-color,transform] active:scale-[0.98]">
        <CreditCard className="w-4 h-4 mr-2" />{option === 'annual' ? 'Get Annual Bundle' : 'Get Flexible Bundle'}
      </Button>
    )}
  </>
);

export const BundlePlanCard = () => {
  const o = useBundleOffer();
  const { user, isAuthenticated, isPro } = useAuth();
  const navigate = useNavigate();
  const [option, setOption] = useState('annual');
  const [checkout, setCheckout] = useState(null);

  useEffect(() => {
    if (o && window.location.hash === '#bundle') document.getElementById('bundle')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [o]);

  if (!o) return <div className="rounded-2xl border-2 border-amber-200 bg-white min-h-[560px] flex items-center justify-center md:col-span-2 lg:col-span-1"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  const note = isAuthenticated ? ownedNote(isPro, addonIsActive(user)) : null;
  const start = () => (isAuthenticated ? setCheckout(option === 'annual' ? 'bundle_annual' : 'bundle_flex')
    : navigate('/login?mode=register&redirect=%2Fpricing%23bundle'));

  return (
    <>
      <PlanCardShell id="bundle" tone="bundle" testId="bundle-pricing-card" className="md:col-span-2 md:max-w-xl md:mx-auto md:w-full lg:col-span-1 lg:max-w-none"
        badge={<><Award className="w-3.5 h-3.5" />Best value</>}
        title="Pro + Officials" subtitle="The full Pro plan plus the Officials Scheduling module."
        control={<PlanToggle value={option} onChange={setOption} tone="amber" testId="bundle-segment"
          options={[['annual', `Annual — Save $${usdNum(o.save)}`], ['flexible', 'Flexible']]} />}
        price={<Swap active={option} items={{ annual: <AnnualPrice o={o} />, flexible: <FlexPrice o={o} /> }} />}
        features={<FeatureList testId="bundle-features" items={OFFICIALS_POINTS}
          lead={<><Crown className="w-4 h-4 text-purple-500 shrink-0 mt-0.5" />Everything in Pro, plus:</>} />}
        footer={<Footer o={o} option={option} note={note} onStart={start} />} />
      <CheckoutDialog open={!!checkout} productId={checkout} onClose={() => setCheckout(null)} />
    </>
  );
};
